import { createConnectorLogger, getErrorMessage } from "../../shared/services/connectorErrors";

const logSlideInsertError = createConnectorLogger("deckorate", "SlideInsertError");

/**
 * Adds a new slide (title + bullet body) to the active presentation.
 *
 * Unlike the shared slideOps.insertTextOnSlide (which returns false and
 * swallows errors), this THROWS a descriptive error on failure so the Create-
 * slides wizard can surface exactly why a slide wasn't added — silent failure
 * is what made the progress list claim success when nothing was inserted.
 */
/**
 * Where `insertSlideFromBase64` puts the slides. Defaults to the end of the
 * deck (what Create Slides wants); the Asset Library asks for the position
 * the user is actually working at.
 *
 *   - "end":      after the last slide.
 *   - "current":  immediately after the currently selected slide (the last one
 *                 when several are selected), falling back to the end when
 *                 nothing is selected or the host can't report a selection.
 *   - { slideId }: immediately after that slide — used to chain several
 *                 inserts so they land in order after the first batch.
 */
export type SlideInsertPosition = "end" | "current" | { readonly slideId: string };

/** How inserted slides are themed — see `insertSlideFromBase64`. */
export type SlideInsertFormatting = "KeepSourceFormatting" | "UseDestinationTheme";

/**
 * Inserts slide(s) from a directly-insertable base64 `.pptx` at the end of the
 * active deck. Throws a descriptive error on failure.
 *
 * `formatting` defaults to "KeepSourceFormatting" — right for the backend's
 * own designed output (or a real template's `.pptx`), which should land
 * looking exactly as designed. A slide assembled around a synthetic skeleton
 * (see service/xmlSlideAssembler.ts) has no meaningful "source" theme of its
 * own, so that caller passes "UseDestinationTheme" instead, letting any
 * scheme-colour reference in the shape XML resolve against the deck it's
 * landing in rather than the skeleton's arbitrary default theme.
 */
export async function insertSlideFromBase64(
  base64Pptx: string,
  formatting: SlideInsertFormatting = "KeepSourceFormatting",
): Promise<void> {
  await insertSlideFromBase64At(base64Pptx, "end", formatting);
}

/**
 * Same insert as `insertSlideFromBase64`, at a chosen position.
 *
 * @returns the id of the last slide this call inserted, when `position` is not
 *   "end" (so a caller can chain the next batch after it); `undefined` for an
 *   end-of-deck insert, which nobody needs to chain from.
 */
export async function insertSlideFromBase64At(
  base64Pptx: string,
  position: SlideInsertPosition,
  formatting: SlideInsertFormatting = "KeepSourceFormatting",
): Promise<string | undefined> {
  const g = globalThis as { PowerPoint?: typeof PowerPoint };
  if (!g.PowerPoint) {
    throw new Error("PowerPoint API unavailable — not running inside PowerPoint.");
  }

  let lastInsertedId: string | undefined;
  await g.PowerPoint.run(async (ctx) => {
    const presentation = ctx.presentation;
    const presAny = presentation as unknown as {
      insertSlidesFromBase64?: (b: string, o?: unknown) => void;
    };
    if (typeof presAny.insertSlidesFromBase64 !== "function") {
      throw new TypeError(
        "This PowerPoint version can't insert slides from base64 (needs PowerPointApi 1.2+).",
      );
    }

    const slides = presentation.slides;
    slides.load("items/id");
    await ctx.sync();

    const idsBefore = slides.items.map((slide) => slide.id);
    const targetId = await resolveTargetSlideId(ctx, idsBefore, position);
    presAny.insertSlidesFromBase64(
      base64Pptx,
      targetId ? { formatting, targetSlideId: targetId } : { formatting },
    );
    await ctx.sync();

    if (position === "end") return;
    slides.load("items/id");
    await ctx.sync();
    const known = new Set(idsBefore);
    const added = slides.items.map((slide) => slide.id).filter((id) => !known.has(id));
    lastInsertedId = added[added.length - 1];
  });
  return lastInsertedId;
}

/** Picks the slide the new slides go after; `undefined` means an empty deck. */
async function resolveTargetSlideId(
  ctx: PowerPoint.RequestContext,
  slideIds: readonly string[],
  position: SlideInsertPosition,
): Promise<string | undefined> {
  const lastId = slideIds[slideIds.length - 1];
  if (position === "end") return lastId;
  if (typeof position === "object") return slideIds.includes(position.slideId) ? position.slideId : lastId;

  try {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items/id");
    await ctx.sync();
    const current = selected.items[selected.items.length - 1]?.id;
    return current && slideIds.includes(current) ? current : lastId;
  } catch (error) {
    // Selection API unavailable on this host (needs PowerPointApi 1.5) — the
    // end of the deck is the safe place to put the slides.
    logSlideInsertError("slide-insert-selected-slides", getErrorMessage(error, "Couldn't read the selected slide"));
    return lastId;
  }
}

/**
 * Adds a new slide on a placeholder-free ("Blank") layout and syncs. The default
 * `slides.add()` uses the master's first layout — usually "Title Slide", which
 * carries "Click to add title/subtitle" prompt placeholders that bleed through
 * any content we draw ourselves. Resolving a Blank layout removes those prompts.
 * Falls back to the host default if the master/layouts can't be read.
 */
export async function addBlankSlide(
  ctx: PowerPoint.RequestContext,
  slides: PowerPoint.SlideCollection,
): Promise<void> {
  const slidesAny = slides as unknown as { add?: (opts?: unknown) => void };
  if (typeof slidesAny.add !== "function") {
    throw new TypeError(
      "This PowerPoint version can't add slides via the API (needs PowerPointApi 1.5+; use PowerPoint on the web or update desktop).",
    );
  }

  let addOpts: { slideMasterId: string; layoutId: string } | undefined;
  try {
    const master = ctx.presentation.slideMasters.getItemAt(0);
    master.load("id");
    const layouts = master.layouts;
    layouts.load("items/id,items/name");
    await ctx.sync();
    // "Blank" is the standard placeholder-free layout; fall back to the last
    // layout (conventionally Blank) when the name doesn't match (locale, etc.).
    const blank =
      layouts.items.find((l) => /blank/i.test(l.name)) ??
      layouts.items[layouts.items.length - 1];
    if (blank) addOpts = { slideMasterId: master.id, layoutId: blank.id };
  } catch (e) {
    // Master/layout not accessible on this host — fall back to the default add.
    logSlideInsertError("create-slide-blank-layout-lookup", getErrorMessage(e, "Blank layout lookup failed; using the default layout"));
  }

  try {
    slidesAny.add(addOpts);
    await ctx.sync();
  } catch (e) {
    // A rejected layoutId is rare; retry with the host default so we still add.
    logSlideInsertError("create-slide-blank-layout-add", getErrorMessage(e, "Blank layout add rejected; retrying with the default layout"));
    slidesAny.add();
    await ctx.sync();
  }
}

export async function addContentSlide(title: string, bullets: string[]): Promise<void> {
  const g = globalThis as { PowerPoint?: typeof PowerPoint };
  if (!g.PowerPoint) {
    throw new Error("PowerPoint API unavailable — not running inside PowerPoint.");
  }

  await g.PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    await addBlankSlide(ctx, slides);

    const count = slides.getCount();
    await ctx.sync();

    const slide = slides.getItemAt(count.value - 1);
    const titleBox = slide.shapes.addTextBox(title || "Untitled", {
      left: 40,
      top: 28,
      width: 880,
      height: 70,
    });
    try {
      titleBox.textFrame.textRange.font.size = 28;
      titleBox.textFrame.textRange.font.bold = true;
    } catch {
      /* font API may be unavailable on this host */
    }

    const body = bullets.filter(Boolean).map((b) => `• ${b}`).join("\n");
    if (body) {
      const bodyBox = slide.shapes.addTextBox(body, {
        left: 40,
        top: 120,
        width: 880,
        height: 360,
      });
      try {
        bodyBox.textFrame.textRange.font.size = 18;
      } catch {
        /* skip */
      }
    }

    await ctx.sync();
  });
}
